import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../openplay/OpenPlayIcon';
import EventCard from '../../pages/interactive-event-map/components/EventCard';

const PlaceDetailModal = ({
  isOpen,
  place,
  events,
  userLocation,
  fallbackEventImage,
  onClose,
  onShowPlaceImage,
  onApply,
  onViewDetails,
}) => {
  if (!isOpen) return null;

  const EVENT_TYPE_LABELS = {
    open_game: '오픈게임',
    lesson: '레슨',
    training: '트레이닝',
    tournament: '대회',
    ad: '광고',
  };

  const uniqueEventTypes = useMemo(() => {
    const set = new Set();
    (events || []).forEach((e) => {
      const t = String(e?.eventType || 'open_game');
      if (t) set.add(t);
    });
    return Array.from(set);
  }, [events]);

  const showTabs = uniqueEventTypes.length > 1;
  const [activeType, setActiveType] = useState('all');

  useEffect(() => {
    if (!showTabs) {
      setActiveType('all');
      return;
    }
    setActiveType((prev) => {
      if (prev === 'all') return prev;
      return uniqueEventTypes.includes(prev) ? prev : 'all';
    });
  }, [showTabs, uniqueEventTypes]);

  const filteredEvents = useMemo(() => {
    const list = Array.isArray(events) ? events : [];
    if (!showTabs || activeType === 'all') return list;
    return list.filter((e) => String(e?.eventType || 'open_game') === activeType);
  }, [activeType, events, showTabs]);

  const tabs =
    showTabs ? (
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setActiveType('all')}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-smooth ${
            activeType === 'all'
              ? 'bg-primary text-primary-foreground border-primary'
              : 'bg-background text-foreground border-border hover:bg-muted'
          }`}
        >
          전체
        </button>
        {uniqueEventTypes.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setActiveType(t)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-smooth ${
              activeType === t
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-background text-foreground border-border hover:bg-muted'
            }`}
          >
            {EVENT_TYPE_LABELS[t] || t}
          </button>
        ))}
      </div>
    ) : null;

  const distanceKm = (a, b) => {
    const toRad = (deg) => (deg * Math.PI) / 180;
    const R = 6371;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const s =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
    return R * c;
  };

  const withDistance = (event) => {
    if (!userLocation) return event;
    const lat = Number(event?.latitude);
    const lng = Number(event?.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return event;
    const d = distanceKm({ lat: userLocation.lat, lng: userLocation.lng }, { lat, lng });
    if (!Number.isFinite(d)) return event;
    const label = `${d.toFixed(d < 10 ? 1 : 0)}km`;
    return { ...(event || {}), distance: label };
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-start justify-center px-4 py-4 md:items-center md:p-6">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[80vh] bg-card rounded-2xl border border-border shadow-elevation-3 overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <p className="text-base font-semibold text-foreground">{place ? place.name : '이 장소'}</p>
            <p className="text-xs text-muted-foreground caption">이벤트 {events.length}개</p>
          </div>
          <button
            type="button"
            className="w-9 h-9 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted"
            onClick={onClose}
            aria-label="닫기"
          >
            <Icon name="XMarkIcon" size={16} />
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-4">
          {place && (() => {
            const gallery = [place.coverImageUrl, ...(place.galleryUrls || [])].map((url) => String(url || '').trim()).filter(Boolean);
            const uniqueGallery = [...new Set(gallery)];
            const previewImage = uniqueGallery[0] || fallbackEventImage;
            const indoorLabel = place.indoor ? '실내' : '실외';
            return (
              <div className="rounded-2xl border border-border overflow-hidden bg-background">
                <div className="p-4 border-b border-border">
                  <div className="flex items-center gap-4">
                    <button
                      type="button"
                      onClick={() => {
                        if (uniqueGallery.length > 0) {
                          onShowPlaceImage?.(0);
                        }
                      }}
                      className="relative w-28 h-28 rounded-xl bg-muted overflow-hidden flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-primary/60"
                      aria-label="코트 이미지 크게 보기"
                    >
                      <img src={previewImage} alt={place.name} className="w-full h-full object-cover" />
                      {uniqueGallery.length > 1 && (
                        <span className="absolute bottom-2 right-2 px-2 py-1 rounded-full bg-black/70 text-white text-[11px] font-semibold">
                          {uniqueGallery.length}장
                        </span>
                      )}
                    </button>
                    <div className="flex-1 min-w-0 space-y-2">
                      <div>
                        <p className="text-base font-semibold text-foreground">{place.name}</p>
                        <p className="text-xs text-muted-foreground mt-1">{place.address}</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <span className="px-2.5 py-1 rounded-full bg-muted/60 text-[11px] font-semibold text-foreground">{indoorLabel}</span>
                        {place.courtCount != null && (
                          <span className="px-2.5 py-1 rounded-full bg-muted/60 text-[11px] font-semibold text-foreground">
                            코트 {place.courtCount}면
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-4 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-muted-foreground">
                    <div className="flex items-center gap-2 rounded-xl bg-muted/40 px-3 py-2">
                      <Icon name="BuildingOfficeIcon" size={16} className="text-foreground" />
                      <span>{indoorLabel}</span>
                    </div>
                    {place.websiteUrl && (
                      <a
                        href={place.websiteUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-2 rounded-xl bg-muted/40 px-3 py-2 hover:bg-muted transition-smooth"
                      >
                        <Icon name="LinkIcon" size={16} className="text-foreground" />
                        <span className="truncate">{place.websiteUrl}</span>
                      </a>
                    )}
                    {place.courtCount != null && (
                      <div className="flex items-center gap-2 rounded-xl bg-muted/40 px-3 py-2">
                        <Icon name="Squares2X2Icon" size={16} className="text-foreground" />
                        <span>코트 {place.courtCount}면</span>
                      </div>
                    )}
                    {place.floorMaterial && (
                      <div className="flex items-center gap-2 rounded-xl bg-muted/40 px-3 py-2">
                        <Icon name="AdjustmentsHorizontalIcon" size={16} className="text-foreground" />
                        <span>{place.floorMaterial}</span>
                      </div>
                    )}
                    {Array.isArray(place.priceInfo) && place.priceInfo.length > 0 && (
                      <div className="flex items-center gap-2 rounded-xl bg-muted/40 px-3 py-2">
                        <Icon name="CurrencyDollarIcon" size={16} className="text-foreground" />
                        <span>{place.priceInfo.join(' · ')}</span>
                      </div>
                    )}
                    {place.supplies && (
                      <div className="flex items-center gap-2 rounded-xl bg-muted/40 px-3 py-2">
                        <Icon name="ClipboardDocumentListIcon" size={16} className="text-foreground" />
                        <span>{place.supplies}</span>
                      </div>
                    )}
                  </div>
                  {place.amenities && (
                    <div className="flex flex-wrap gap-3 text-xs">
                      {place.amenities?.parking && (
                        <span className="inline-flex items-center gap-2 rounded-xl border border-border bg-white px-3 py-2 text-foreground">
                          <Icon name="TruckIcon" size={18} className="text-primary" />
                          주차
                        </span>
                      )}
                      {place.amenities?.shower && (
                        <span className="inline-flex items-center gap-2 rounded-xl border border-border bg-white px-3 py-2 text-foreground">
                          <Icon name="SparklesIcon" size={18} className="text-blue-500" />
                          샤워
                        </span>
                      )}
                      {place.amenities?.ballMachine && (
                        <span className="inline-flex items-center gap-2 rounded-xl border border-border bg-white px-3 py-2 text-foreground">
                          <Icon name="BoltIcon" size={18} className="text-amber-500" />
                          볼머신
                        </span>
                      )}
                      {place.equipmentRental && (
                        <span className="inline-flex items-center gap-2 rounded-xl border border-border bg-white px-3 py-2 text-foreground">
                          <Icon name="CubeIcon" size={18} className="text-emerald-500" />
                          장비대여
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {place && showTabs && <div className="pt-1">{tabs}</div>}

          {!place && showTabs && <div className="pb-1">{tabs}</div>}
          {events.length === 0 && <div className="text-sm text-muted-foreground">등록된 이벤트가 없습니다.</div>}
          {events.length > 0 && filteredEvents.length === 0 && (
            <div className="text-sm text-muted-foreground">해당 조건의 이벤트가 없습니다.</div>
          )}
          {filteredEvents.map((event) => (
            <EventCard
              key={event.id}
              event={{
                ...withDistance(event),
                coverImageUrl: String(event?.coverImageUrl || '').trim() || fallbackEventImage,
              }}
              onApply={() => onApply(event.id)}
              onViewDetails={() => onViewDetails(event.id)}
              variant="modal"
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default PlaceDetailModal;
